import { useMemo, useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { ContentCopy, ExpandLess, ExpandMore, CheckCircleOutline } from "@mui/icons-material";

type Props = {
  payload: any;
  title?: string;
  maxHeight?: number | string;
};

export const PayloadDock = ({ payload, title = "Payload (debug)", maxHeight = 280 }: Props) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const pretty = useMemo(() => {
    try {
      return JSON.stringify(payload ?? {}, null, 2);
    } catch {
      return String(payload ?? "");
    }
  }, [payload]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(pretty);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {}
  };

  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 12,
        mt: 3,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.10)",
        boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
        backgroundColor: "#0B0F19",
      }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 13 }}>{title}</Typography>

          <Typography sx={{ color: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.55)", fontSize: 12 }}>{open ? "Click to collapse" : "Click to expand"}</Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={copied ? "Copied" : "Copy"}>
            <IconButton
              onClick={handleCopy}
              size="small"
              sx={{
                color: copied ? "#22C55E" : "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.85)",
                "&:hover": { color: "#fff" },
              }}
            >
              {copied ? <CheckCircleOutline fontSize="small" /> : <ContentCopy fontSize="small" />}
            </IconButton>
          </Tooltip>

          <IconButton size="small" sx={{ color: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.85)" }}>
            {open ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {open && (
        <Box
          sx={{
            px: 2,
            pb: 2,
            maxHeight,
            overflow: "auto",
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: 12,
            lineHeight: 1.55,
            color: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.88)",
            whiteSpace: "pre",
          }}
        >
          {pretty}
        </Box>
      )}
    </Box>
  );
};
